<template>
  <Teleport to="body">
    <div v-if="showModal" class="fixed inset-0 bg-[#0000009e] flex items-center justify-center z-50"
      @click.self="closeModal">
      <div
        class="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-all duration-300 ease-in-out" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-2xl font-bold">{{ mode === 'create' ? 'Créer le projet' : 'Mettre à jour le projet' }}</h2>
          <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
            <XIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Formulaire de création de projet -->
        <form class="space-y-4" @submit.prevent="handleSubmit">
          <div>
            <label for="title" class="block text-sm font-medium text-gray-700">Titre *</label>

            <!-- Projet HAL sélectionné - Interface améliorée -->
            <div v-if="selectedProject" class="mt-1 mb-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <h4 class="font-medium text-blue-900">{{ selectedProject.acronym_s || selectedProject.label_s }}
                    </h4>
                    <span :class="{
                      'bg-blue-100 text-blue-800': selectedProject.type === 'Européen',
                      'bg-green-100 text-green-800': selectedProject.type === 'ANR'
                    }" class="px-2 py-1 text-xs font-medium rounded-full">
                      {{ selectedProject.type }}
                    </span>
                  </div>
                  <p class="text-sm text-blue-700 line-clamp-2">{{ selectedProject.label_s }}</p>
                  <div v-if="selectedProject.financing_s" class="text-xs text-blue-600 mt-1">
                    Financement: {{ selectedProject.financing_s }}
                  </div>
                </div>
                <!-- Actions : Reset -->
                <div class="flex items-center gap-1 ml-3">
                  <button type="button"
                    class="p-1.5 text-blue-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Retirer la sélection HAL" @click="resetSearch">
                    <XIcon class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Input de recherche -->
            <div class="relative">
              <input id="title" ref="input_field" v-model="searchQuery" type="text"
                class="mt-1 block w-full pr-8 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                :placeholder="selectedProject ? 'Titre du projet (modifiable)' : 'Rechercher un projet ANR ou Européen...'"
                required @input="handleSearchInput" @focus="handleInputFocus" @blur="handleInputBlur" />
              <button v-if="searchQuery && !selectedProject" type="button"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-600"
                @click="resetSearch">
                <XIcon class="h-4 w-4" />
              </button>
            </div>

            <!-- Liste des suggestions HAL -->
            <div v-if="showSuggestions && (projectSuggestions.length > 0 || isLoading || showNoResults)"
              ref="suggestions"
              class="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto"
              :style="suggestionStyle">
              <div v-if="isLoading" class="p-3 text-center text-gray-600">
                <div class="flex items-center justify-center">
                  <svg class="animate-spin h-5 w-5 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  Recherche en cours...
                </div>
              </div>
              <div v-else-if="showNoResults" class="p-3 text-center text-gray-600">
                Aucun projet trouvé
              </div>
              <div v-else>
                <div v-for="project in projectSuggestions" :key="project.docid"
                  class="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
                  @click="selectProject(project)">
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <div v-if="project.acronym_s" class="font-medium text-sm text-gray-900">
                        {{ project.acronym_s }}
                      </div>
                      <div class="text-xs text-gray-600 truncate">{{ project.label_s }}</div>
                      <div v-if="project.financing_s" class="text-xs text-gray-500 mt-1">
                        {{ project.financing_s }}
                      </div>
                    </div>
                    <span :class="{
                      'bg-blue-100 text-blue-800': project.type === 'Européen',
                      'bg-green-100 text-green-800': project.type === 'ANR'
                    }" class="ml-2 px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap">
                      {{ project.type }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label for="type" class="block text-sm font-medium text-gray-700">Type</label>
            <input id="type" v-model="projectData.type" type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          <div>
            <label for="acronym" class="block text-sm font-medium text-gray-700">Acronyme</label>
            <input id="acronym" v-model="projectData.acronym" type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          <div>
            <label for="aurehal_id" class="block text-sm font-medium text-gray-700">Aurehal ID</label>
            <input id="aurehal_id" v-model="projectData.aurehal_id" type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          <div>
            <label for="fundedby" class="block text-sm font-medium text-gray-700">Financement</label>
            <input id="fundedby" v-model="projectData.fundedby" type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          <div>
            <label for="link" class="block text-sm font-medium text-gray-700">Lien de référence (DOI, URL, etc.)</label>
            <input id="link" v-model="projectData.link" type="text"
              placeholder="https://doi.org/10.1000/example ou https://exemple.com"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>

          <div>
            <label for="description" class="block text-sm font-medium text-gray-700">Description</label>
            <textarea id="description" v-model="projectData.description" rows="3"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"></textarea>
          </div>

          <div class="flex justify-end">
            <button type="submit"
              class="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
              {{ mode === 'create' ? 'Créer' : 'Mettre à jour' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de confirmation -->
    <ModalAreYouSure
      v-model="showConfirmModal"
      title="Modifications non sauvegardées"
      message="Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?"
      confirm-text="Fermer sans sauvegarder"
      cancel-text="Continuer l'édition"
      @confirm="confirmClose"
      @cancel="cancelClose"
    />

    <!-- Notification -->
    <Notification ref="notification" />
  </Teleport>
</template>

<script lang="ts">

import { XIcon } from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { projects } from "../../api/projects";
import type { Project, ProjectCreate } from '../../api/types/entities/project/types';
import type { HalProject, ProjectSuggestion } from "../../types/global";
import Notification from '../Notification.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Fonction de debounce
function debounce<T extends (query: string) => Promise<void>>(
  func: T,
  wait: number
): (query: string) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return function (query: string) {
    const later = (): void => {
      clearTimeout(timeout);
      func(query);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default defineComponent({
  name: "ProjectsCreator",
  components: {
    XIcon,
    Notification,
    ModalAreYouSure
  },
  props: {
    displayType: {
      type: String,
      default: 'card',
      validator: (value: string) => ['card', 'button'].includes(value)
    },
    userId: {
      type: String,
      required: true
    },
    mode: {
      type: String as PropType<'create' | 'edit'>,
      default: 'create'
    },
    initialData: {
      type: Object as PropType<Project>,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['project-created', 'project-updated', 'update:show', 'is_closed'],
  setup(props, { emit }) {


    const showModal = ref(props.show);
    const isLoading = ref(false);
    const searchQuery = ref('');
    const showSuggestions = ref(false);
    const showNoResults = ref(false);
    const projectSuggestions = ref<ProjectSuggestion[]>([]);
    const selectedProject = ref<ProjectSuggestion | null>(null);
    const inputWidth = ref(0);
    const input_field = ref<HTMLInputElement | null>(null);
    const notification = ref<InstanceType<typeof Notification> | null>(null);
    const showConfirmModal = ref(false);
    const initialDataSnapshot = ref<ProjectCreate | null>(null);

    const projectData = reactive<ProjectCreate>({
      user_id: props.userId,
      title: "",
      type: "",
      acronym: "",
      aurehal_id: "",
      description: "",
      fundedby: "",
      link: "",
      is_public: true,
      is_deleted: false
    });

    // S'assurer que user_id est toujours défini
    watch(() => props.userId, (newUserId) => {
      if (newUserId) {
        projectData.user_id = newUserId;
      }
    }, { immediate: true });

    // Surveiller les changements de la prop show
    watch(() => props.show, (newValue) => {
      showModal.value = newValue;
    });

    // Surveiller les changements de showModal  
    watch(showModal, (newValue) => {
      emit('update:show', newValue);
      // Reset form quand on ouvre le modal en mode création
      if (newValue && props.mode === 'create' && !props.initialData) {
        resetForm();
      }
    });

    // Initialiser les données si en mode édition
    watch(() => props.initialData, (newData) => {
      if (props.mode === 'edit' && newData) {
        Object.assign(projectData, {
          user_id: newData.user_id,
          title: newData.title,
          type: newData.type,
          acronym: newData.acronym,
          aurehal_id: newData.aurehal_id,
          description: newData.description,
          fundedby: newData.fundedby,
          link: newData.link
        });
        searchQuery.value = newData.title;
      }
    }, { immediate: true });

    // Ajout : watcher pour pré-remplir à chaque ouverture de la modale avec les données d'import, quel que soit le mode
    watch([
      () => props.initialData,
      showModal
    ], ([newData, modalVisible]) => {
      if (modalVisible && newData) {
        console.log('[ProjectsCreator] Pré-remplissage avec initialData:', JSON.parse(JSON.stringify(newData)));
        Object.assign(projectData, {
          user_id: newData.user_id || props.userId,
          uuid: newData.uuid || undefined, // Récupérer l'UUID s'il est fourni, sinon undefined
          title: newData.title || '',
          type: newData.type || '',
          acronym: newData.acronym || '',
          aurehal_id: newData.aurehal_id || '',
          fundedby: newData.fundedby || '',
          link: newData.link || '',
          description: newData.description || '',
          is_public: newData.is_public ?? true
        });
        // Synchroniser searchQuery avec le titre
        searchQuery.value = newData.title || '';
        // Sauvegarder un snapshot des données initiales pour la comparaison
        initialDataSnapshot.value = JSON.parse(JSON.stringify(projectData));
        console.log('[ProjectsCreator] Formulaire après assignation :', JSON.parse(JSON.stringify(projectData)));
        console.log('[ProjectsCreator] searchQuery synchronisé :', searchQuery.value);
      } else {
        console.log('[ProjectsCreator] Watcher déclenché mais pas de données à appliquer (modalVisible =', modalVisible, ', initialData =', newData, ')');
        // Réinitialiser le snapshot quand le modal se ferme
        if (!modalVisible) {
          initialDataSnapshot.value = null;
        }
      }
    }, { immediate: true });

    // Fonction pour vérifier s'il y a des modifications
    const hasModifications = (): boolean => {
      if (!initialDataSnapshot.value) {
        // En mode création, vérifier si des champs ont été remplis
        return !!(projectData.title || projectData.type || projectData.acronym || 
                 projectData.aurehal_id || projectData.description || projectData.fundedby || 
                 projectData.link);
      }
      
      // En mode édition, comparer avec les données initiales
      const snapshot = initialDataSnapshot.value;
      return projectData.title !== snapshot.title ||
             projectData.type !== snapshot.type ||
             projectData.acronym !== snapshot.acronym ||
             projectData.aurehal_id !== snapshot.aurehal_id ||
             projectData.description !== snapshot.description ||
             projectData.fundedby !== snapshot.fundedby ||
             projectData.link !== snapshot.link ||
             projectData.is_public !== snapshot.is_public;
    };

    const closeModal = (): void => {
      if (hasModifications()) {
        showConfirmModal.value = true;
      } else {
        showModal.value = false;
        resetForm();
        emit('update:show', false);
        emit('is_closed');
      }
    };

    const confirmClose = (): void => {
      showConfirmModal.value = false;
      showModal.value = false;
      resetForm();
      emit('update:show', false);
      emit('is_closed');
    };

    const cancelClose = (): void => {
      showConfirmModal.value = false;
    };

    const resetForm = (): void => {
      searchQuery.value = '';
      selectedProject.value = null;
      projectSuggestions.value = [];
      showSuggestions.value = false;
      showNoResults.value = false;

      Object.assign(projectData, {
        user_id: props.userId,
        title: "",
        type: "",
        acronym: "",
        aurehal_id: "",
        description: "",
        fundedby: "",
        link: "",
        is_public: true,
        is_deleted: false
      });
      // Réinitialiser le snapshot
      initialDataSnapshot.value = null;
    };

    const handleSubmit = async (): Promise<void> => {
      try {
        if (props.mode === 'create') {
          const response = await projects.createProject(projectData);
          emit('project-created', response);
          resetForm();
          showModal.value = false;
          emit('update:show', false);
          emit('is_closed');
        } else {
          const response = await projects.updateProject(props.initialData?.uuid || '', projectData);
          emit('project-updated', response);
          resetForm();
          showModal.value = false;
          emit('update:show', false);
          emit('is_closed');
        }
      } catch (error) {
        console.error(`Erreur lors de ${props.mode === 'edit' ? 'la mise à jour' : 'la création'} du projet:`, error);
      }
    };


    const suggestionStyle = computed((): Record<string, string> => {
      if (!input_field.value) {
        return {};
      }

      const rect = input_field.value.getBoundingClientRect();
      return {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`
      };
    });

    const updateInputWidth = (): void => {
      if (input_field.value) {
        inputWidth.value = input_field.value.offsetWidth;
      }
    };

    // Mettre à jour la largeur au montage et lors du redimensionnement
    onMounted(() => {
      updateInputWidth();
      window.addEventListener('resize', updateInputWidth);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateInputWidth);
    });

    const cleanLabel = (label: string): string => {
      // Supprime tout ce qui suit le premier crochet ouvert, y compris le crochet lui-même
      return label.replace(/\s*\[[^\]]*\].*/, '').trim();
    };

    const searchProjects = debounce(async (query: string): Promise<void> => {
      console.log('🔍 [searchProjects] Recherche démarrée pour:', query);

      if (query.length < 3) {
        console.log('🔍 [searchProjects] Requête trop courte, arrêt');
        projectSuggestions.value = [];
        showSuggestions.value = false;
        isLoading.value = false;
        showNoResults.value = false;
        return;
      }

      console.log('🔍 [searchProjects] Début de la recherche HAL');
      isLoading.value = true;
      showSuggestions.value = true;
      showNoResults.value = false;

      console.log('🔍 [searchProjects] État - showSuggestions:', showSuggestions.value, 'isLoading:', isLoading.value);

      try {
        const searchTerm = query.endsWith('*') ? query : `${query}*`;
        console.log('🔍 [searchProjects] Terme de recherche:', searchTerm);

        const [anrResponse, europeanResponse] = await Promise.all([
          fetch(`https://api.archives-ouvertes.fr/ref/anrproject/?q=${encodeURIComponent(searchTerm)}&wt=json&fl=docid,label_s,acronym_s,financing_s,reference_s`),
          fetch(`https://api.archives-ouvertes.fr/ref/europeanproject/?q=${encodeURIComponent(searchTerm)}&wt=json&fl=docid,label_s,acronym_s,financing_s,reference_s`)
        ]);

        console.log('🔍 [searchProjects] Réponses reçues - ANR:', anrResponse.status, 'Européen:', europeanResponse.status);

        const [anrData, europeanData] = await Promise.all([
          anrResponse.json(),
          europeanResponse.json()
        ]);

        console.log('🔍 [searchProjects] Données ANR:', anrData);
        console.log('🔍 [searchProjects] Données Européennes:', europeanData);

        const europeanProjects = (europeanData.response?.docs || []).map((doc: HalProject) => ({
          ...doc,
          label_s: cleanLabel(doc.label_s),
          type: 'Européen' as const
        }));

        const anrProjects = (anrData.response?.docs || []).map((doc: HalProject) => ({
          ...doc,
          label_s: cleanLabel(doc.label_s),
          type: 'ANR' as const
        }));

        projectSuggestions.value = [...europeanProjects, ...anrProjects];
        console.log('🔍 [searchProjects] Suggestions finales:', projectSuggestions.value.length, 'projets trouvés');
        console.log('🔍 [searchProjects] Première suggestion:', projectSuggestions.value[0]);

        // Vérifier si les deux API retournent numFound = 0
        const noEuropeanResults = europeanData.response?.numFound === 0;
        const noAnrResults = anrData.response?.numFound === 0;
        showNoResults.value = noEuropeanResults && noAnrResults;

        console.log('🔍 [searchProjects] showNoResults:', showNoResults.value);
        console.log('🔍 [searchProjects] showSuggestions final:', showSuggestions.value);

      } catch (error) {
        console.error('🔍 [searchProjects] Erreur lors de la recherche de projets:', error);
        projectSuggestions.value = [];
        showNoResults.value = true;
      } finally {
        isLoading.value = false;
        console.log('🔍 [searchProjects] Recherche terminée - showSuggestions:', showSuggestions.value, 'suggestions:', projectSuggestions.value.length);
      }
    }, 500);

    const handleSearchInput = (event: Event): void => {
      console.log('📝 [handleSearchInput] Saisie détectée');
      const input = event.target as HTMLInputElement;
      console.log('📝 [handleSearchInput] Valeur de l\'input:', input.value);
      searchQuery.value = input.value;
      projectData.title = input.value;

      // S'assurer que showSuggestions est activé pour les recherches
      if (input.value.length >= 3) {
        showSuggestions.value = true;
        console.log('📝 [handleSearchInput] Activation des suggestions');
      }

      searchProjects(input.value);
    };

    const selectProject = (project: ProjectSuggestion): void => {
      selectedProject.value = project;
      searchQuery.value = project.label_s;
      showSuggestions.value = false;

      Object.assign(projectData, {
        title: project.label_s,
        type: project.type,
        acronym: project.acronym_s || '',
        aurehal_id: project.docid,
        fundedby: project.financing_s || '',
        link: project.reference_s || ''
      });

      console.log('🏗️ [selectProject] Projet sélectionné:', project);
      console.log('🏗️ [selectProject] Données mises à jour:', projectData);
    };

    const resetSearch = (): void => {
      selectedProject.value = null;
      searchQuery.value = '';
      projectSuggestions.value = [];
      showSuggestions.value = false;
      showNoResults.value = false;

      // Réinitialiser les champs projet
      Object.assign(projectData, {
        title: "",
        type: "",
        acronym: "",
        aurehal_id: "",
        fundedby: "",
        link: ""
      });
    };

    const handleInputFocus = (): void => {
      showSuggestions.value = true;
    };

    const handleInputBlur = (): void => {
      // Délai pour permettre le clic sur les suggestions
      setTimeout(() => {
        showSuggestions.value = false;
      }, 200);
    };

    return {
      showModal,
      isLoading,
      searchQuery,
      showSuggestions,
      showNoResults,
      projectSuggestions,
      selectedProject,
      projectData,
      inputWidth,
      input_field,
      notification,
      closeModal,
      handleSubmit,
      handleSearchInput,
      selectProject,
      resetSearch,
      handleInputFocus,
      handleInputBlur,
      suggestionStyle,
      showConfirmModal,
      confirmClose,
      cancelClose
    };
  }
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Forcer les styles pour tous les inputs dans ProjectsCreator */
input[type="text"],
input[type="email"],
input[type="url"],
input[type="password"],
input[type="number"],
input:not([type]),
textarea,
select {
  margin-bottom: 8px !important;
  border: 1px solid #d1d5db !important;
  /* border-gray-300 */
  border-radius: 6px !important;
  /* rounded-md */
  padding: 8px 12px !important;
  /* px-3 py-2 */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  /* shadow-sm */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  background-color: white !important;
  width: 100% !important;
  display: block !important;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="url"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input:not([type]):focus,
textarea:focus,
select:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  /* border-blue-500 */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  /* ring-blue-500 */
}

input::placeholder,
textarea::placeholder {
  color: #9ca3af !important;
  /* text-gray-400 */
}

/* Labels */
label {
  margin-bottom: 4px !important;
  display: block !important;
  font-weight: 500 !important;
  color: #374151 !important;
  /* text-gray-700 */
  font-size: 0.875rem !important;
  /* text-sm */
}
</style>